import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Form, Button, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { Eye, EyeOff, CheckCircle, ArrowLeft } from 'lucide-react';
import './ResetPassword.css';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!token) {
            setError('Token de recuperação inválido ou ausente.');
        }
    }, [token]);

    const validatePassword = (password: string): string[] => {
        const errors: string[] = [];
        if (password.length < 8) {
            errors.push('A senha deve ter no mínimo 8 caracteres');
        }
        if (!/[A-Za-z]/.test(password)) {
            errors.push('A senha deve conter pelo menos 1 letra');
        }
        if (!/\d/.test(password)) {
            errors.push('A senha deve conter pelo menos 1 número');
        }
        return errors;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Validações locais
        const validationErrors = validatePassword(newPassword);
        if (validationErrors.length > 0) {
            setError(validationErrors.join('. '));
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('As senhas não coincidem.');
            return;
        }

        setLoading(true);

        try {
            await api.post('/company/auth/password-reset/confirm/', {
                token,
                new_password: newPassword,
                new_password_confirm: confirmPassword
            });
            setSuccess(true);
        } catch (err: any) {
            const errorMsg = err.response?.data?.error ||
                           err.response?.data?.new_password?.[0] ||
                           'Erro ao redefinir senha. O link pode ter expirado.';
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="reset-password-page">
                <div className="reset-password-container">
                    <div className="reset-password-image-panel">
                        <img
                            src="/agendainicioGold.png"
                            alt="Y-Agenda"
                            className="reset-password-hero-image"
                        />
                    </div>

                    <div className="reset-password-form-panel">
                        <div className="reset-password-form-wrapper">
                            <div className="reset-password-brand-header">
                                <h1 className="reset-password-brand-title">Y-Agenda</h1>
                                <p className="reset-password-brand-subtitle">Gestão inteligente de agendamentos</p>
                            </div>
                            <div className="success-icon-container">
                                <CheckCircle size={48} className="success-icon" />
                            </div>
                            <div className="reset-password-form-header">
                                <h2>Senha alterada!</h2>
                                <p>Sua senha foi redefinida com sucesso.</p>
                                <p className="text-muted small">
                                    Você já pode fazer login com sua nova senha.
                                </p>
                            </div>
                            <Button
                                variant="primary"
                                size="lg"
                                className="w-100 btn-primary"
                                onClick={() => navigate('/login')}
                            >
                                Ir para o login
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="reset-password-page">
            <div className="reset-password-container">
                <div className="reset-password-image-panel">
                    <img
                        src="/agendainicioGold.png"
                        alt="Y-Agenda"
                        className="reset-password-hero-image"
                    />
                </div>

                <div className="reset-password-form-panel">
                    <div className="reset-password-form-wrapper">
                        <div className="reset-password-brand-header">
                            <h1 className="reset-password-brand-title">Y-Agenda</h1>
                            <p className="reset-password-brand-subtitle">Gestão inteligente de agendamentos</p>
                        </div>
                        <div className="reset-password-form-header">
                            <h2>Nova Senha</h2>
                            <p>Crie uma nova senha para sua conta</p>
                        </div>

                        {error && (
                            <Alert variant="danger" className="mb-4">
                                {error}
                            </Alert>
                        )}

                        <Form onSubmit={handleSubmit}>
                            <Form.Group className="mb-3" controlId="formNewPassword">
                                <Form.Label>Nova Senha</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Mínimo 8 caracteres, 1 letra e 1 número"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                        disabled={loading || !token}
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setShowPassword(!showPassword)}
                                        tabIndex={-1}
                                        className="btn-outline-secondary"
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </Button>
                                </InputGroup>
                                <Form.Text className="text-muted">
                                    Mínimo 8 caracteres, com pelo menos 1 letra e 1 número
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-4" controlId="formConfirmPassword">
                                <Form.Label>Confirmar Senha</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        placeholder="Digite a senha novamente"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        disabled={loading || !token}
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        tabIndex={-1}
                                        className="btn-outline-secondary"
                                    >
                                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </Button>
                                </InputGroup>
                            </Form.Group>

                            <div className="d-grid">
                                <Button
                                    variant="primary"
                                    type="submit"
                                    size="lg"
                                    disabled={loading || !token}
                                    className="btn-primary"
                                >
                                    {loading ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Processando...
                                        </>
                                    ) : (
                                        'Redefinir Senha'
                                    )}
                                </Button>
                            </div>
                        </Form>

                        <div className="text-center mt-4">
                            <Link to="/login" className="back-link">
                                <ArrowLeft size={18} className="me-1" />
                                Voltar para o login
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
